import { useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Filter, Package, Search } from 'lucide-react';
import {
  BosDurum,
  Girdi,
  Rozet,
  Tablar,
  TarihAraligiSecici,
  VeriTablo,
  tarihIso,
  type ColumnDef,
  type TarihAraligi,
} from '@emanetx/ui';
import type { Kiralama, KiralamaDurumu } from '@emanetx/sdk';
import { useKiralamaListesi } from './api.js';
import { DURUM_ETIKET, DURUM_TONU, kalanSureyiBicimle, telefonMaskele } from './durumlar.js';
import { KiralamaDetayPenceresi } from './kiralama-detay-penceresi.js';

type AltSekme = 'aktif' | 'gecmis' | 'tumu';

interface KiralamaSearch {
  sekme?: AltSekme;
  kira?: string;
  arama?: string;
  durum?: KiralamaDurumu;
  noktaId?: string;
}

/** Varsayilan: son 30 gun. */
function sonOtuzGun(): TarihAraligi {
  const e = new Date();
  const s = new Date();
  s.setDate(s.getDate() - 29);
  s.setHours(0, 0, 0, 0);
  return { baslangic: s, bitis: e };
}

export function SayfaKiralamalar() {
  const search = useSearch({ strict: false }) as KiralamaSearch;
  const navigate = useNavigate();
  const [sayfa, setSayfa] = useState(1);
  const [araliki, setAralik] = useState<TarihAraligi>(sonOtuzGun);

  const altSekme: AltSekme = search.sekme ?? 'aktif';
  const aktifKiraId = search.kira;
  const arama = search.arama ?? '';
  const durum = search.durum;

  const sorgu = {
    sayfa,
    boyut: 50,
    ...(arama && { arama }),
    ...(durum && { durum }),
    ...(altSekme === 'aktif' && { aktifMi: true }),
    ...(altSekme === 'gecmis' && { aktifMi: false }),
    ...(tarihIso(araliki.baslangic) && { baslangicTen: tarihIso(araliki.baslangic) }),
    ...(tarihIso(araliki.bitis) && { baslangicAKadar: tarihIso(araliki.bitis) }),
  };

  const liste = useKiralamaListesi(sorgu);

  const guncelle = (yeni: Partial<KiralamaSearch>) => {
    void navigate({
      to: '/panel/kiralamalar',
      search: { ...search, ...yeni },
    });
  };

  const kolonlar = useMemo<ColumnDef<Kiralama, unknown>[]>(
    () => [
      {
        accessorKey: 'kod',
        header: 'Kod',
        cell: ({ row }) => (
          <span className="text-marka-700 dark:text-marka-400 font-mono text-xs font-semibold">
            {row.original.kod}
          </span>
        ),
      },
      {
        id: 'musteri',
        header: 'Müşteri',
        meta: { etiket: 'Müşteri' },
        cell: ({ row }) => (
          <div>
            <div className="text-fume-700 dark:text-fume-300 font-mono text-xs">
              {telefonMaskele(row.original.telefon)}
            </div>
            {row.original.adSoyad && (
              <div className="text-fume-400 text-xs">{row.original.adSoyad}</div>
            )}
          </div>
        ),
      },
      {
        id: 'noktaDolap',
        header: 'Nokta / Dolap',
        meta: { etiket: 'Nokta / Dolap' },
        cell: ({ row }) => (
          <div className="text-xs">
            <span className="text-fume-500 font-mono">{row.original.noktaKod ?? '—'}</span>
            {row.original.dolapNumara && (
              <span className="text-fume-700 dark:text-fume-300 font-mono">
                {' / #'}
                {row.original.dolapNumara}
              </span>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'baslangic',
        header: 'Başlangıç',
        meta: { etiket: 'Başlangıç' },
        cell: ({ row }) => (
          <span className="text-fume-500 text-xs">
            {new Date(row.original.baslangic).toLocaleString('tr-TR', {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        ),
      },
      {
        id: 'kalan',
        header: 'Kalan',
        meta: { etiket: 'Kalan süre' },
        enableSorting: false,
        cell: ({ row }) => {
          const kalan = kalanSureyiBicimle(row.original.planlananBitis, row.original.bitis ?? null);
          return (
            <span
              className={
                kalan.asti
                  ? 'font-mono text-xs font-semibold text-red-600 dark:text-red-400'
                  : 'text-fume-700 dark:text-fume-300 font-mono text-xs'
              }
            >
              {kalan.metin}
            </span>
          );
        },
      },
      {
        accessorKey: 'toplamTutar',
        header: 'Tutar',
        meta: { etiket: 'Tutar' },
        cell: ({ row }) => {
          const sembol =
            row.original.paraBirimi === 'TRY' ? '₺' : row.original.paraBirimi === 'EUR' ? '€' : '$';
          return (
            <span className="font-mono text-xs">
              {sembol}
              {row.original.toplamTutar}
            </span>
          );
        },
        sortingFn: (a, b) => Number(a.original.toplamTutar) - Number(b.original.toplamTutar),
      },
      {
        accessorKey: 'durum',
        header: 'Durum',
        meta: { etiket: 'Durum' },
        cell: ({ row }) => (
          <Rozet ton={DURUM_TONU[row.original.durum]} nokta>
            {DURUM_ETIKET[row.original.durum]}
          </Rozet>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Kiralamalar</h1>
        <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
          Tüm noktalardaki aktif ve geçmiş kiralama kayıtları.
        </p>
      </header>

      <Tablar
        ogeler={[
          {
            deger: 'aktif',
            etiket: 'Aktif',
            sayac: altSekme === 'aktif' ? liste.data?.toplam : undefined,
          },
          { deger: 'gecmis', etiket: 'Geçmiş' },
          { deger: 'tumu', etiket: 'Tümü' },
        ]}
        aktif={altSekme}
        onAktifDegisti={(d) => {
          setSayfa(1);
          guncelle({ sekme: d as AltSekme });
        }}
      />

      <VeriTablo
        veri={liste.data?.veriler ?? []}
        kolonlar={kolonlar}
        toplam={liste.data?.toplam}
        sayfa={sayfa}
        boyut={50}
        onSayfa={setSayfa}
        yukleniyor={liste.isLoading}
        yenileniyor={liste.isFetching}
        onYenile={() => void liste.refetch()}
        onSatirTikla={(k) => guncelle({ kira: k.id })}
        bosDurum={
          <BosDurum
            ikon={Package}
            baslik={
              altSekme === 'aktif'
                ? 'Şu an aktif kiralama yok'
                : altSekme === 'gecmis'
                  ? 'Bu tarih aralığında geçmiş kiralama yok'
                  : 'Bu kriterlerle kiralama yok'
            }
            aciklama="Tarih aralığını veya filtreyi değiştirin."
          />
        }
        ustAksiyonlar={
          <>
            <div className="relative min-w-0 flex-1 sm:max-w-xs">
              <Search
                size={14}
                className="text-fume-400 pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
              />
              <Girdi
                placeholder="Kod, telefon, ad, nokta..."
                value={arama}
                onChange={(e) => {
                  setSayfa(1);
                  guncelle({ arama: e.target.value || undefined });
                }}
                className="h-10 pl-9 text-sm"
              />
            </div>
            <TarihAraligiSecici
              deger={araliki}
              onDegisim={(a) => {
                setAralik(a);
                setSayfa(1);
              }}
              etiketYok
              className="h-10 text-xs"
            />
            <div className="flex items-center gap-2">
              <Filter size={13} className="text-fume-400" />
              <select
                value={durum ?? ''}
                onChange={(e) => {
                  setSayfa(1);
                  guncelle({ durum: (e.target.value as KiralamaDurumu) || undefined });
                }}
                className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-10 rounded-lg border bg-white px-3 text-xs focus:outline-none focus:ring-2"
              >
                <option value="">Tüm durumlar</option>
                <option value="OLUSTURULDU">Oluşturuldu</option>
                <option value="ODEME_BEKLIYOR">Ödeme Bekliyor</option>
                <option value="AKTIF">Aktif</option>
                <option value="DOLAP_ACILDI">Dolap Açıldı</option>
                <option value="KULLANILIYOR">Kullanılıyor</option>
                <option value="TESLIM_ALINIYOR">Teslim Alınıyor</option>
                <option value="TAMAMLANDI">Tamamlandı</option>
                <option value="IPTAL_EDILDI">İptal</option>
                <option value="SURESI_GECTI">Süresi Geçti</option>
                <option value="SORUNLU">Sorunlu</option>
              </select>
            </div>
          </>
        }
      />

      <KiralamaDetayPenceresi
        kiralamaId={aktifKiraId}
        onKapat={() => guncelle({ kira: undefined })}
      />
    </div>
  );
}
